import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  rectIntersection,
} from "@dnd-kit/core";
import { SearchContext } from "context/providers";
import { LOADING_STATES, SKILL_TYPES } from "lookup";
import { useContext, useEffect, useMemo, useState } from "react";
import { Button } from "components/ui/button";
import ReasoningTooltip from "./ReasoningTooltip";
import SavedSearches from "components/SavedSearches";
import { LoaderCircle, RotateCcw } from "lucide-react";
import { useLocation, useParams } from "react-router-dom-v5-compat";
import { TABS } from "context/constants/common";
import ChipDragOverlay from "./ChipDragOverlay";
import { cn } from "utils";

export default function Wrapper({
  handleDragEnd,
  handleDragStart,
  handleDragCancel,
  selectedSkill,
  children,
}) {
  const {
    searchState,
    handleLoadAISkills,
    setSkillSearchState,
    loadingAISearch,
  } = useContext(SearchContext);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  const [activeId, setActiveId] = useState(null);
  const [dragTransform, setDragTransform] = useState(null);
  const routerParams = useParams();

  useEffect(() => {
    if (!activeId) {
      setTimeout(() => {
        setDragTransform(null);
      }, 200);
    }
  }, [activeId]);

  const skillType = useMemo(() => {
    const skillRequired = searchState?.skills?.required.find(
      (skill) => skill.name === activeId
    );
    const skillType = skillRequired
      ? SKILL_TYPES.REQUIRED
      : SKILL_TYPES.OPTIONAL;
    return skillType;
  }, [searchState, activeId]);

  const handleReset = async () => {
    const result = await handleLoadAISkills({
      refresh: true,
      paramsId: routerParams.id,
    });
    if (!result) return;
    setSkillSearchState({ ...result.skills });
  };

  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const currentTab = params.get("tab");

  const displayLoader = loadingAISearch === LOADING_STATES.LOADING;

  const aiAgentDisabled =
    (currentTab && currentTab !== TABS.SEARCHRESULTS) ||
    searchState.basicSearch;

  const aiAgentUninteractable = aiAgentDisabled || displayLoader;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={(event) => {
        setActiveId(event.active.id);
        handleDragStart(event);
      }}
      onDragEnd={(event) => {
        setActiveId(null);
        handleDragEnd(event);
      }}
      onDragMove={({ delta }) => {
        setDragTransform({ x: delta.x, y: delta.y });
      }}
      onDragAbort={handleDragCancel}
      onDragCancel={handleDragCancel}
    >
      <div id="search-agent">
        <header
          id="search-agent-header"
          className="min-h-[64px] bg-blue-50 rounded-md rounded-b-none px-0 py-3 flex gap-8 items-center"
        >
          <div
            className={cn("flex gap-8 items-center transition-opacity w-full", {
              "pointer-events-none opacity-50": aiAgentUninteractable,
            })}
          >
            <h2 className="font-bold text-2xl">Search Agent</h2>
            {aiAgentDisabled ? null : (
              <>
                <ReasoningTooltip />
                <Button
                  variant="ghost"
                  onClick={handleReset}
                  className="text-base hover:text-sky-700 text-sky-600 font-bold gap-1 hover:bg-sky-100"
                >
                  <RotateCcw size={20} />
                  Reset
                </Button>
                {displayLoader && (
                  <div className="flex justify-center items-center animate-spin text-sky-600 z-50">
                    <LoaderCircle strokeWidth={2.5} />
                  </div>
                )}
                <div className="ml-auto">
                  <SavedSearches />
                </div>
              </>
            )}
          </div>
          {aiAgentDisabled && (
            <p className="w-full flex justify-end text-slate-700 ml-auto font-bold opacity-100 z-10">
              {searchState.basicSearch
                ? 'Turn off "Basic Search” to use Intelligent Search'
                : ""}
            </p>
          )}
        </header>

        <div
          className={cn(
            "rounded-md border-t-0 border border-slate-300 px-6 py-3 flex flex-col gap-4 transition-opacity",
            {
              "pointer-events-none opacity-50": aiAgentUninteractable,
            }
          )}
        >
          <div
            id="search-agent-contents"
            className="flex flex-col w-full h-full md:flex-row gap-8"
          >
            {children}
          </div>
          <footer id="search-agent-footer" className="text-xs text-slate-400">
            AI can make mistakes. Please always double check the reasoning.
          </footer>
        </div>

        <DragOverlay id="search-agent-overlay">
          <ChipDragOverlay
            skill={selectedSkill}
            skillType={skillType}
            transform={dragTransform}
          />
        </DragOverlay>
      </div>
    </DndContext>
  );
}
